import { useState } from 'react'
import { usePortalAuth } from '../auth/authContexts'
import { useApi } from '../services/api'

export type SyncMfaPhoneResult = {
  enrolled: boolean
  maskedPhone: string | null
  /** True only after `loginWithMfaEnrollment` resolves (syncAndChallenge path). */
  challengeSucceeded: boolean
}

function syncFailureMessage(err: unknown): string {
  const e = err as Error & { apiBody?: { message?: string } }
  return e?.apiBody?.message ?? e?.message ?? 'Something went wrong. Please try again.'
}

/**
 * Shared “I’ve updated my number” flow: sync Entsia phone → Auth0 SMS enroll,
 * with an optional follow-on MFA challenge helper.
 */
export function useSyncMfaPhone(): {
  isSyncing: boolean
  errorMessage: string | null
  /** Sync/enroll only (no Auth0 challenge). */
  syncPhone: () => Promise<SyncMfaPhoneResult>
  /** Sync/enroll then Auth0 MFA challenge (challenge errors do not undo enroll). */
  syncAndChallenge: () => Promise<SyncMfaPhoneResult>
} {
  const { loginWithMfaEnrollment } = usePortalAuth()
  const { syncMfaPhone } = useApi()
  const [isSyncing, setIsSyncing] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const runSyncPhone = async (): Promise<Omit<SyncMfaPhoneResult, 'challengeSucceeded'>> => {
    const result = await syncMfaPhone()
    if (result.smsMfaEnrolled) {
      return {
        enrolled: true,
        maskedPhone: result.smsMfaMaskedPhone ?? null,
      }
    }
    setErrorMessage(
      result.message ??
        'We still could not find a valid Australian mobile on your policy. Please call customer service.',
    )
    return { enrolled: false, maskedPhone: null }
  }

  const syncPhone = async (): Promise<SyncMfaPhoneResult> => {
    setErrorMessage(null)
    setIsSyncing(true)
    try {
      const syncResult = await runSyncPhone()
      return { ...syncResult, challengeSucceeded: false }
    } catch (err: unknown) {
      setErrorMessage(syncFailureMessage(err))
      return { enrolled: false, maskedPhone: null, challengeSucceeded: false }
    } finally {
      setIsSyncing(false)
    }
  }

  const syncAndChallenge = async (): Promise<SyncMfaPhoneResult> => {
    setErrorMessage(null)
    setIsSyncing(true)
    try {
      let syncResult: Omit<SyncMfaPhoneResult, 'challengeSucceeded'>
      try {
        syncResult = await runSyncPhone()
      } catch (err: unknown) {
        setErrorMessage(syncFailureMessage(err))
        return { enrolled: false, maskedPhone: null, challengeSucceeded: false }
      }
      if (!syncResult.enrolled) {
        return { ...syncResult, challengeSucceeded: false }
      }
      try {
        await loginWithMfaEnrollment({ returnTo: '/dashboard' })
        return { ...syncResult, challengeSucceeded: true }
      } catch (err: unknown) {
        const detail = syncFailureMessage(err)
        setErrorMessage(
          `Your mobile was accepted, but verification failed. ${detail}`,
        )
        return { ...syncResult, challengeSucceeded: false }
      }
    } finally {
      setIsSyncing(false)
    }
  }

  return { isSyncing, errorMessage, syncPhone, syncAndChallenge }
}
